import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ElementRef,
  OnInit,
  OnDestroy,
  TemplateRef,
  Optional,
  Inject,
} from '@angular/core';
import { FocusableOption } from '@angular/cdk/a11y';
import { PhxKpiChangeType, PhxKpiChangeTypeEnum } from './phx-overview.types';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';
import { calculateChange } from './phx-overview.utils';
import { Subscription } from 'rxjs';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../locale/phx-locale.token';

@Component({
  selector: 'phx-overview-item',
  styleUrls: ['./phx-overview-item.component.scss'],
  templateUrl: './phx-overview-item.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'phx-overview-item',
    '[class.phx-overview-item-clickable]': 'phxClickable',
    '[class.phx-overview-item-disabled]': 'phxDisabled',
    '(click)': 'onItemClicked()',
    '[tabIndex]': '-1',
  },
})
export class PhxOverviewItemComponent implements OnInit, OnChanges, OnDestroy, FocusableOption {
  /**
   * The label text for the overview item
   */
  @Input() public phxLabel: string | null = null;

  /**
   * The current numeric value of the KPI
   */
  @Input() public phxValue: number = 0;

  /**
   * The previous numeric value for comparison
   */
  @Input() public phxPreviousValue: number | null = null;

  /**
   * Optional subtext to display below the value
   */
  @Input() public phxSubtext: string | null = null;

  /**
   * If true, decreasing values are treated as positive (green)
   * and increasing values as negative (red).
   * Useful for metrics where lower is better (e.g., accidents, costs)
   */
  @Input() public phxInverted: boolean = false;

  /**
   * If the change needs a neutral color (neither positive nor negative)
   */
  @Input() public phxNeutralChange: boolean = false;

  /**
   * Whether to show percentage change instead of absolute change
   */
  @Input() public phxShowPercentageChange: boolean = false;

  /**
   * Whether the item is disabled
   */
  @Input() public phxDisabled: boolean = false;

  /**
   * Whether the item is clickable/selectable
   */
  @Input() public phxClickable: boolean = true;

  /**
   * Tooltip content to display on hover
   */
  @Input() public phxLabelTooltipContent: string | TemplateRef<any> | null = null;

  /**
   * Emits when a clickable item is clicked
   */
  @Output() public phxItemClicked: EventEmitter<void> = new EventEmitter<void>();

  public changeValue: number = 0;
  public absoluteChangeValue: number | string = 0;
  public percentageChangeValue: number | string = 0;
  public changeType: PhxKpiChangeType = PhxKpiChangeTypeEnum.NoChange;
  public isMobileView: boolean = false;
  public readonly iconSize: number = 12;

  private _breakpointObserverSubscription!: Subscription;

  constructor(
    private _phxBreakpointObserver: PhxBreakpointObserverService,
    private _cdr: ChangeDetectorRef,
    private _elementRef: ElementRef,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null
  ) {}

  ngOnInit(): void {
    this._breakpointObserverSubscription = this._phxBreakpointObserver?.smMobile.subscribe(isMobile => {
      this.isMobileView = isMobile;
      this._cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxValue, phxPreviousValue } = changes;
    if (phxValue || phxPreviousValue) {
      this.setupValues();
    }
  }

  ngOnDestroy(): void {
    this._breakpointObserverSubscription.unsubscribe();
  }

  public focus(): void {
    this._elementRef.nativeElement?.focus();
  }

  public onItemClicked(): void {
    if (this.phxDisabled || !this.phxClickable) return;
    this.phxItemClicked.emit();
  }

  public getChangeContainerClass(): string {
    return `phx-overview-item-${this.changeType}`;
  }

  public formatValue(value: number): string {
    if(this.phxLocale) {
      return this.phxLocale.formatCompactNumber(value);
    }
    return value.toFixed(0);

  }

  private setupValues(): void {
    const previousValue = this.phxPreviousValue;
    const value = this.phxValue;
    if (previousValue === null || previousValue === undefined || value === null || value === undefined) {
      this.changeType = PhxKpiChangeTypeEnum.NoChange;
      return;
    }

    const { changeValue, absoluteChangeValue, percentageChangeValue, changeType } = calculateChange(
      value,
      previousValue,
      this.phxInverted,
      this.phxNeutralChange,
    );

    this.changeValue = changeValue;
    this.absoluteChangeValue = this.phxLocale ? this.phxLocale.formatNumber(absoluteChangeValue) : absoluteChangeValue;
    this.percentageChangeValue = this.phxLocale ? this.phxLocale.formatPercentage(percentageChangeValue / 100) : `${percentageChangeValue}%`;
    this.changeType = changeType;
  }
}
